import { collection, documentId, getDocs, query, where, limit } from '@firebase/firestore';
import { httpsCallable } from '@firebase/functions';
import { db, functions } from './firebase';
import { COLLECTIONS } from './collections';
import { InsuranceChecklist } from '../types';
import type { ClientStructure } from '../types/api';
import { mapDocs } from '@utils/firestore';
import {
  InsuranceChecklistSchema,
  StructureSchema,
  StructureClaimSchema,
} from '../schemas/firestoreSchemas';

// Re-export so existing consumers (agentClientsStore, agent screens) keep
// their import path; the type itself lives in types/api.
export type { ClientStructure } from '../types/api';

/**
 * Agent-facing queries. An agent's clients are derived from the claims whose
 * `insurerChecklistId` points to one of that insurer's checklists.
 */
export const agentsApi = {
  async listInsurerChecklists(insurerName: string): Promise<InsuranceChecklist[]> {
    const q = query(
      collection(db, COLLECTIONS.INSURANCE_CHECKLISTS),
      where('insurerName', '==', insurerName)
    );
    const snapshot = await getDocs(q);
    return mapDocs(snapshot, InsuranceChecklistSchema);
  },

  async listClientStructures(insurerName: string, maxResults = 100): Promise<ClientStructure[]> {
    const { clients } = await agentsApi.listClientStructuresWithChecklists(insurerName, maxResults);
    return clients;
  },

  /**
   * Same bounded fetch, but also returns the insurer checklists it already
   * queried to build the `in` list — zero extra reads for callers that need
   * checklist names (e.g. the send-report confirm step).
   */
  async listClientStructuresWithChecklists(
    insurerName: string,
    maxResults = 100
  ): Promise<{ clients: ClientStructure[]; checklists: InsuranceChecklist[] }> {
    const checklists = await agentsApi.listInsurerChecklists(insurerName);
    if (checklists.length === 0) return { clients: [], checklists: [] };

    const ids = checklists.map((c) => c.id).slice(0, 30);
    const q = query(
      collection(db, COLLECTIONS.STRUCTURE_CLAIMS),
      where('insurerChecklistId', 'in', ids),
      limit(maxResults)
    );
    const claimsSnap = await getDocs(q);
    const claims = mapDocs(claimsSnap, StructureClaimSchema);

    // Batch-fetch the structure docs via documentId-in queries (≤30 ids
    // each, run in parallel) — the previous shape was one sequential getDoc
    // per claim: 100 reads + 100 round trips at the maxResults cap.
    const IN_LIMIT = 30;
    const structuresCol = collection(db, COLLECTIONS.STRUCTURES);
    const chunks: string[][] = [];
    for (let i = 0; i < claims.length; i += IN_LIMIT) {
      chunks.push(claims.slice(i, i + IN_LIMIT).map((c) => c.id));
    }
    const snaps = await Promise.all(
      chunks.map((ids) => getDocs(query(structuresCol, where(documentId(), 'in', ids))))
    );
    const structuresById = new Map(
      snaps.flatMap((snap) => mapDocs(snap, StructureSchema).map((s) => [s.id, s] as const))
    );

    // Preserve claims-query order when assembling.
    const clients: ClientStructure[] = [];
    for (const claim of claims) {
      const structure = structuresById.get(claim.id);
      if (structure) {
        clients.push({ structure, claim });
      }
    }
    return { clients, checklists };
  },
};

interface ProvisionAgentProfileResult {
  insurerName: string;
  inviteCode: string;
  status: 'pending';
}

/**
 * Create the calling user's agent profile (in a 'pending', unverified
 * state) via Cloud Function. The client can no longer write agentProfile
 * directly — Firestore rules forbid it — so self-service agent signup
 * routes the profile write through here. Insurance privilege stays locked
 * until an admin runs verifyAgent.
 */
export async function provisionAgentProfile(
  insurerName: string
): Promise<ProvisionAgentProfileResult> {
  const callable = httpsCallable<{ insurerName: string }, ProvisionAgentProfileResult>(
    functions,
    'provisionAgentProfile'
  );
  const result = await callable({ insurerName: insurerName.trim() });
  return result.data;
}
